import fromTypes from "../actions/types";
import * as fromOrganisations from "features/organisations";
import * as fromSites from "features/sites/actions/types";
import {
	onSiteInspectionChecklistListReceive,
	onSiteInspectionChecklistListFailed,
} from "../actions";
import { ofType } from "redux-observable";
import { mergeMap, catchError, filter, switchMap, delay } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { of } from "rxjs";
import { authHeaders, tokenExists, getOrganisationBaseUrl } from "frame";

export const siteInspectionChecklistListEffect = (action$, state$) =>
	action$.pipe(
		ofType(
			fromTypes.SITE_INSPECTION_CHECKLIST_LIST_REQUESTED,
			fromOrganisations.ORGANISATIONS_CURRENT_ORGANISATION_CHANGED,
			fromSites.SITES_DETAILS
		),
		filter(() => tokenExists()),
		filter(() => !!state$.value.organisations.currentOrganisation),
		filter(() => state$.value.queue.hasNetwork),
		filter(() => !!state$.value.sites && !!state$.value.sites.currentSite),
		switchMap(() => {
			const organisationId = state$.value.organisations.currentOrganisation.id;
			const site = state$.value.sites.currentSite;
			if (!site || !site.id) {
				console.error("No site ID available for site-specific checklist");
				return of(onSiteInspectionChecklistListFailed(new Error("No site ID available")));
			}
			
			console.log("Fetching site-specific checklists for site ID:", site.id);
			return ajax
				.get(
					`${getOrganisationBaseUrl(
						state$.value
					)}/site-inspection-checklists/all`,
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						console.log("Site-specific checklist response:", ajaxResponse.response);
						return of(
							onSiteInspectionChecklistListReceive(
								ajaxResponse.response
							)
						);
					}),
					catchError((error) => {
						console.error("Error fetching site-specific checklists:", error);
						return of(onSiteInspectionChecklistListFailed(error));
					})
				)
		})
	);

export const siteInspectionChecklistPagedListEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.SITE_INSPECTION_CHECKLIST_PAGED_LIST_REQUESTED),
		delay(300),
		filter(() => {
			if (!tokenExists()) {
				console.log("SITE PAGED EFFECT: Token does not exist");
				return false;
			}
			return true;
		}),
		filter(() => {
			if (!state$ || !state$.value) {
				console.log("SITE PAGED EFFECT: State not available");
				return false;
			}
			return true;
		}),
		switchMap(({ payload }) => {
			console.log("SITE PAGED EFFECT: Processing paged checklist request", payload);
			const pagingParams = payload || { pageNumber: 1, pageSize: 10 };
			
			// Check if navigating away (component unmounting)
			if (payload && payload.__cancelled) {
				console.log("SITE PAGED EFFECT: Request cancelled due to navigation");
				return of();
			}
			
			try {
				if (!state$.value.organisations || !state$.value.organisations.currentOrganisation) {
					console.warn("SITE PAGED EFFECT: No current organization available", state$.value.organisations);
					// Don't return an error here, just skip this request
					return of();
				}
				
				const organisationId = state$.value.organisations.currentOrganisation.id;
				if (!organisationId) {
					console.warn("SITE PAGED EFFECT: No organization ID available");
					// Don't return an error here, just skip this request
					return of();
				}
				
				if (!state$.value.queue || !state$.value.queue.hasNetwork) {
					console.log("SITE PAGED EFFECT: Network not available");
					return of(onSiteInspectionChecklistListFailed(new Error("Network not available")));
				}
				
				// This is a library/global view - we don't need site info
				// Check if this is a site-specific request by looking for siteId in payload
				const isSiteSpecific = payload && payload.siteId;
				
				// If site-specific but no currentSite is available, skip (don't error)
				if (isSiteSpecific && (!state$.value.sites || !state$.value.sites.currentSite)) {
					console.log("SITE PAGED EFFECT: Site-specific request but no current site available");
					return of();
				}
				
				// Prepare the API request payload
				let requestPayload = { ...pagingParams };
				
				// Only add siteId if this is a site-specific request
				if (isSiteSpecific && state$.value.sites.currentSite) {
					requestPayload.siteId = state$.value.sites.currentSite.id;
					console.log("SITE PAGED EFFECT: Fetching site-specific checklists for site ID:", requestPayload.siteId);
				} else {
					console.log("SITE PAGED EFFECT: Fetching global checklists for organization:", organisationId);
				}
				
				return ajax
					.post(
						`${getOrganisationBaseUrl(state$.value)}/site-inspection-checklists/paged`,
						requestPayload,
						authHeaders()
					)
					.pipe(
						mergeMap((ajaxResponse) => {
							console.log("SITE PAGED EFFECT: Checklist response received:", 
								ajaxResponse.response ? 
								`${ajaxResponse.response.results?.length || 0} items` : 
								"No response data");
							return of(
								onSiteInspectionChecklistListReceive(
									ajaxResponse.response
								)
							);
						}),
						catchError((error) => {
							console.error("SITE PAGED EFFECT: Error fetching checklists:", error);
							return of(onSiteInspectionChecklistListFailed(error));
						})
					);
			} catch (error) {
				console.error("SITE PAGED EFFECT: Unexpected error in effect:", error);
				return of(onSiteInspectionChecklistListFailed(error));
			}
		})
	); 